import React, { useRef, useCallback, useMemo, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Dialog, DialogActions, DialogContent, Grid } from "@mui/material";
import { Box, Button, TextField, Checkbox, DateTimeStandard } from "@curves/ui-base";
import { ProductInfo, GradientColorHeader, ReadTextField, ScrollableTable } from '@curves/ui-shared';
// TODO: 改為庫存與 icoach 共用的 EmployeeSelect 後統一匯入路徑
import { EmployeeSelect } from "@icoach/components";
import { callRef, ensureArray, isFunction, refIsRequiredError } from "@util/utils";
import { parseDate, getNowTime } from "@util/moment";
import useSelection from "@util/hook/useSelection";
import useGetEmployeeOptionState from "@util/hook/useGetEmployeeOptionState";
import useClubInventoryApi from "@apis/useClubInventoryApi";
import useClubStockOutApi from "@apis/useClubStockOutApi";

// 功能：建立瑕疵領用 payload item
const toClaimItem = row => ({ clubDefectiveReportID: Number(row?.clubDefectiveReportID) });

// 功能：API 瑕疵紀錄欄位 → 表格 row（含 clubDefectiveReportID 供勾選）
const toDefectRow = item => ({
	clubDefectiveReportID: item.clubDefectiveReportID,
	reportDate: item.reportDate,
	assignee: item.employeeName,
	description: item.reportReason,
	thumbnailUrl: item.defectiveImagePath ?? "",
});

/**
 * 瑕疵領用彈窗
 * Props: open, onClose, sourceData, refresh
 * sourceData: { productID, variantID }；商品資訊與瑕疵清單由 API 取得。
 */
const DefectClaimDialog = ({ open, onClose, sourceData = null, refresh }) => {
	// 功能：表單欄位 ref（建單人、原因）
	const creatorRef = useRef(null);
	const descriptionRef = useRef(null);
	const { employeeOption } = useGetEmployeeOptionState();

	const productID = sourceData?.productID != null ? Number(sourceData.productID) : null;
	const variantID = sourceData?.variantID != null ? Number(sourceData.variantID) : 0;

	const [productData, setProductData] = useState({ detail: null, defectList: [] });
	const { detail, defectList } = productData;

	const { getClubInventoryDetailApi, getClubInventoryDefectiveRecordsApi } = useClubInventoryApi();
	const { postClubStockOutDefectiveConsumptionApi } = useClubStockOutApi();

	// 功能：開啟且具 productID 時載入商品明細與瑕疵紀錄；關閉時清空
	useEffect(() => {
		let isCancelled = false;

		const fetchProductDetailAndDefectRecords = async () => {
			if (!open || productID == null) {
				setProductData({ detail: null, defectList: [] });
				return;
			}
			try {
				const detailResp = await getClubInventoryDetailApi({ productID, variantID });
				if (isCancelled) return;
				setProductData(prev => ({ ...prev, detail: detailResp ?? null }));

				const recordsResp = await getClubInventoryDefectiveRecordsApi({ productID, variantID });
				if (isCancelled) return;
				setProductData(prev => ({
					...prev,
					defectList: ensureArray(recordsResp).map(toDefectRow),
				}));
			} catch (e) {
				if (!isCancelled) setProductData({ detail: null, defectList: [] });
			}
		};

		fetchProductDetailAndDefectRecords();
		return () => {
			isCancelled = true;
		};
	}, [open, productID, variantID, getClubInventoryDetailApi, getClubInventoryDefectiveRecordsApi]);

	const productInfo = detail
		? {
				sku: detail.sku ?? "",
				name: detail.productName ?? "",
				spec: detail.variantName ?? "",
				imageUrl: detail.pictureSrc ?? "",
			}
		: null;

	// 功能：清單勾選狀態
	const { selectedIds, selectedCount, selectedItems, isAllSelected, toggleSelection, toggleSelectAll, clearSelection } = useSelection(defectList, "clubDefectiveReportID");
	const isIndeterminate = selectedCount > 0 && !isAllSelected;

	const cellRenderContext = { selectedIds, isAllSelected, isIndeterminate, toggleSelection, toggleSelectAll };

	// 功能：關閉時清空勾選狀態並呼叫 onClose
	const handleClose = useCallback(
		e => {
			clearSelection();
			if (isFunction(onClose)) onClose(e);
		},
		[onClose, clearSelection],
	);

	// 功能：點選 row 切換勾選
	const handleRowClick = useCallback(
		row => {
			const id = row?.clubDefectiveReportID;
			if (id != null) toggleSelection(id);
		},
		[toggleSelection],
	);

	// 功能：表格欄位定義（含全選 Checkbox）
	const headerRow = useMemo(
		() => [
			{
				cellKey: "select",
				headerLabel: <Checkbox checked={isAllSelected} indeterminate={isIndeterminate} onChange={toggleSelectAll} onClick={e => e.stopPropagation()} />,
				minWidth: 48,
				align: "center",
				className: "p-0",
				formatCell: (_, row, { selectedIds, toggleSelection }) => {
					if (!selectedIds) return null;
					const id = row?.clubDefectiveReportID;
					if (id == null) return null;
					return <Checkbox checked={selectedIds.has(id)} onChange={() => toggleSelection(id)} onClick={e => e.stopPropagation()} />;
				},
			},
			{
				cellKey: "reportDate",
				headerLabel: "回報日期",
				minWidth: 110,
				align: "center",
				formatCell: value => (value ? parseDate(value, DateTimeStandard.DisplayDateBySlash) : "—"),
			},
			{ cellKey: "assignee", headerLabel: "擔當", minWidth: 100 },
			{ cellKey: "description", headerLabel: "瑕疵狀況說明", minWidth: 180 },
		],
		[isAllSelected, isIndeterminate, toggleSelectAll],
	);

	// 功能：驗證必填與勾選非空 → 組 payload 送出，成功則刷新並關閉
	const handleSubmit = async e => {
		e.preventDefault();
		if (refIsRequiredError(creatorRef, descriptionRef)) return;
		if (selectedCount <= 0) return;

		const payload = {
			stockOutDate: getNowTime(),
			employeeID: Number(callRef(creatorRef, "getResult")),
			reason: String(callRef(descriptionRef, "getResult") || "").trim(),
			items: ensureArray(selectedItems).map(toClaimItem),
		};

		const resp = await postClubStockOutDefectiveConsumptionApi(payload);
		if (resp) {
			isFunction(refresh) && refresh();
			handleClose();
		}
	};

	if (!open) return null;

	return (
		<Dialog className="simple-content dialog" PaperProps={{ className: "wx-90rem" }} open={open} onClose={handleClose} fullWidth maxWidth="md">
			<GradientColorHeader onClose={handleClose}>瑕疵領用</GradientColorHeader>
			<DialogContent className="p-3">
				<Box component="form" onSubmit={handleSubmit} className="defect-claim-form">
					<Grid container spacing={3} alignItems="flex-start">
						<Grid xs={4} item className="defect-claim-form-left">
							<Grid container spacing={2}>
								<Grid item xs={12}>
									<ProductInfo product={productInfo} layout="row" className="defect-stock-in-product-info" />
								</Grid>
								<Grid item xs={12}>
									<ReadTextField label="建單日期">{parseDate(new Date(), DateTimeStandard.DisplayDateBySlash)}</ReadTextField>
								</Grid>
								<Grid item xs={12}>
									<EmployeeSelect label="建單人" ref={creatorRef} options={employeeOption} fullWidth required />
								</Grid>
								<Grid item xs={12}>
									<TextField label="原因" ref={descriptionRef} placeholder="請填寫領用原因" multiline minRows={3} fullWidth required />
								</Grid>
							</Grid>
						</Grid>
						<Grid xs={8} item className="defect-claim-form-list">
							<ScrollableTable
								headerRow={headerRow}
								rowsData={defectList}
								rowKey="clubDefectiveReportID"
								rowOnClick={handleRowClick}
								rowOnCheckActiveState={row => row?.clubDefectiveReportID != null && selectedIds.has(row.clubDefectiveReportID)}
								cellRenderContext={cellRenderContext}
								tableClassName="theme-gray"
							/>
						</Grid>
					</Grid>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button variant="contained" type="submit" onClick={handleSubmit} disabled={selectedCount <= 0}>
					確認領用
				</Button>
			</DialogActions>
		</Dialog>
	);
};

DefectClaimDialog.propTypes = {
	open: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	sourceData: PropTypes.shape({
		productID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		variantID: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	}),
	refresh: PropTypes.func,
};

export default DefectClaimDialog;
